// Booking Payment Service for Frankenfloss Booking System
// Handles PayPal payment integration for bookings

import { API_BASE_URL } from '../config/api.config';

// ===== Types =====

export interface InitiatePaymentRequest {
  bookingId: number;
  paymentProvider: 'PayPal' | 'Stripe' | 'Klarna' | 'BankTransfer';
  returnUrl: string;
  cancelUrl: string;
}

export interface PaymentInitiateResponse {
  success: boolean;
  paymentUrl?: string;
  paymentId?: string;
  amount: number;
  invoiceNumber?: string;
  errorMessage?: string;
}

export interface PaymentStatusResponse {
  bookingId: number;
  payments: PaymentInfo[];
  invoices: InvoiceInfo[];
}

export interface PaymentInfo {
  id: number;
  amount: number;
  status: string;
  provider: string;
  createdAt: string;
}

export interface InvoiceInfo {
  invoiceNumber: string;
  amount: number;
  status: string;
  dueDate: string;
  type: string;
}

export interface CapturePaymentRequest {
  bookingId: number;
  payPalOrderId: string;
}

export interface CapturePaymentResponse {
  success: boolean;
  bookingId: number;
  bookingNumber?: string;
  amount: number;
  status: string;
  message: string;
}

// ===== Helper Functions =====

function getAuthToken(): string | null {
  return localStorage.getItem('authToken');
}

async function apiRequest<T>(
  method: string,
  endpoint: string,
  data?: any
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  const token = getAuthToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method,
    headers,
    body: data ? JSON.stringify(data) : undefined,
  });

  if (!response.ok) {
    // Try to get error details from response
    let errorMessage = `HTTP ${response.status} ${response.statusText}`;
    try {
      const contentType = response.headers.get('content-type');
      if (contentType?.includes('application/json')) {
        const errorData = await response.json();
        errorMessage = errorData.message || errorData.errorMessage || errorData.title || JSON.stringify(errorData);
      } else {
        const textError = await response.text();
        if (textError) {
          errorMessage = textError;
        }
      }
    } catch {
      // Keep default error message
    }
    console.error('API Error:', errorMessage);
    throw new Error(errorMessage);
  }

  return response.json();
}

// ===== Booking Payment API =====

export const bookingPaymentService = {
  /**
   * Initiate PayPal payment for a booking
   * Creates a PayPal order and returns the approval URL
   */
  async initiatePayPalPayment(
    bookingId: number,
    returnUrl: string,
    cancelUrl: string
  ): Promise<PaymentInitiateResponse> {
    return apiRequest<PaymentInitiateResponse>('POST', '/payment/initiate', {
      bookingId,
      paymentProvider: 1, // PayPal = 1 in PaymentMethod enum
      returnUrl,
      cancelUrl,
    });
  },

  /**
   * Initiate Stripe payment for a booking
   * Creates a Stripe Checkout session and returns the redirect URL
   */
  async initiateStripePayment(
    bookingId: number,
    returnUrl: string,
    cancelUrl: string
  ): Promise<PaymentInitiateResponse> {
    return apiRequest<PaymentInitiateResponse>('POST', '/payment/initiate', {
      bookingId,
      paymentProvider: 0, // Stripe = 0 in PaymentMethod enum
      returnUrl,
      cancelUrl,
    });
  },

  /**
   * Initiate Klarna payment for a booking
   * Creates a Klarna session and returns the redirect URL
   */
  async initiateKlarnaPayment(
    bookingId: number,
    returnUrl: string,
    cancelUrl: string
  ): Promise<PaymentInitiateResponse> {
    return apiRequest<PaymentInitiateResponse>('POST', '/payment/initiate', {
      bookingId,
      paymentProvider: 2, // Klarna = 2 in PaymentMethod enum
      returnUrl,
      cancelUrl,
    });
  },

  /**
   * Capture PayPal payment after customer approval
   * Called when customer returns from PayPal
   */
  async capturePayPalPayment(
    bookingId: number,
    payPalOrderId: string
  ): Promise<CapturePaymentResponse> {
    return apiRequest<CapturePaymentResponse>('POST', '/payment/capture', {
      bookingId,
      payPalOrderId,
    });
  },

  /**
   * Get payment status for a booking
   */
  async getPaymentStatus(bookingId: number): Promise<PaymentStatusResponse> {
    return apiRequest<PaymentStatusResponse>('GET', `/payment/status/${bookingId}`);
  },

  /**
   * Get available payment providers
   */
  async getAvailableProviders(): Promise<Record<string, string>> {
    return apiRequest<Record<string, string>>('GET', '/payment/providers');
  },
};

// ===== Helper Functions =====

/**
 * Format amount in cents to German currency format
 * @param amountInCents Amount in cents (e.g., 39997 = 399,97 €)
 */
export function formatCurrency(amountInCents: number): string {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
  }).format(amountInCents / 100);
}

export default bookingPaymentService;
